import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePagination } from "@/hooks/usePagination";
import { Input } from "@/components/ui/input";
import { Img } from "@/components/ui/img";
import {
  MagnifyingGlass,
  Users,
  X,
  Funnel,
  Translate,
  CaretDown,
  CaretRight,
  Monitor,
  VideoCamera,
  MapPin,
  Check,
  Star,
} from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import type { TherapistListItem } from "@/types";
import {
  BOSNIAN_CODE,
  getAvailablePublicLanguageFilters,
  parsePublicLanguageFilter,
} from "@/lib/languages";
import { publicTherapistsListQuery } from "@/api-queries/therapists";
import { PROBLEM_TAGS, PROBLEM_TAG_LABELS, isProblemTag } from "@/lib/matching/taxonomy";
import { defineSection } from "@/ssr/define-section";
import { ssrPage } from "@/ssr/define-page";
import { definePageMeta } from "@/ssr/define-meta";
import { buildPageStructuredData } from "@/ssr/structured-data";

const therapistsMeta = definePageMeta({
  titleKey: "therapists.meta.title",
  descriptionKey: "therapists.meta.description",
  ogImage: "/og-therapists.jpg",
  structuredData: (ctx) => buildPageStructuredData(ctx, {
    titleKey: "therapists.meta.title",
    descriptionKey: "therapists.meta.description",
    ogImage: "/og-therapists.jpg",
    pageType: "CollectionPage",
    breadcrumbs: [
      { name: "Psihotron", path: "/" },
      { name: ctx.t("nav.therapists"), path: "/therapists" },
    ],
  }),
});

type Therapist = TherapistListItem;

// Compact pill on mobile (auto width, rounded-full, horizontal scroller),
// full field on md+ (fixed width, rounded-xl). Active pills tint on mobile only.
const filterPillTrigger =
  "!h-9 w-full min-w-0 gap-1.5 rounded-full border border-border/60 bg-background px-3.5 text-sm shadow-xs md:!h-12 md:w-48 md:gap-2 md:rounded-xl";
const filterPillActive =
  "max-md:border-primary/30 max-md:bg-primary/10 max-md:text-primary max-md:[&_svg]:text-primary";

function TherapistsIndexPageView() {
  const { t } = useTranslation();
  const { problemTag, language } = Route.useSearch();
  const navigate = useNavigate();

  const {
    searchQuery: search,
    debouncedSearch,
    setSearch: handleSearchChange,
  } = usePagination();

  // SSR prefetches publicTherapistsListQuery (full unfiltered list).
  // On the client we re-query with filters when search/problemTag/language are set,
  // falling back to the prefetched data when there are no active filters.
  const hasFilters = Boolean(debouncedSearch || problemTag || language);

  const { data: therapistsAll = [] } = useQuery(publicTherapistsListQuery);

  const { data: therapistsFiltered = [], isLoading: isLoadingFiltered } = useQuery({
    queryKey: ["therapists", debouncedSearch, problemTag, language],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (problemTag) params.set("problemTag", problemTag);
      if (language) params.set("language", language);
      const res = await fetch(`/api/therapists?${params}`);
      if (!res.ok) throw new Error("Failed to fetch therapists");
      return res.json() as Promise<Therapist[]>;
    },
    placeholderData: (prev) => prev,
    enabled: hasFilters,
  });

  const therapists: Therapist[] = hasFilters ? therapistsFiltered : therapistsAll;
  const isLoading = hasFilters ? isLoadingFiltered : false;

  const languageOptions = getAvailablePublicLanguageFilters(therapistsAll)
    .map((c) => ({ code: c, name: t(`languages.names.${c}`) }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const activeLanguageName = language
    ? t(`languages.names.${language}`)
    : null;

  const activeTopicName = isProblemTag(problemTag) ? PROBLEM_TAG_LABELS[problemTag] : null;
  const hasActiveChips = Boolean(problemTag || language);

  const clearAll = () => {
    handleSearchChange("");
    navigate({ to: "/therapists", search: {} });
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden pt-10 pb-20 sm:pt-12 sm:pb-24">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(160deg, oklch(96% 0.03 84), oklch(98% 0.015 90) 50%, oklch(95% 0.04 80))",
          }}
        />
        <div
          className="pointer-events-none absolute -right-32 -top-32 size-96 rounded-full opacity-15 blur-3xl"
          style={{ background: "var(--color-brand)" }}
        />

        <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <h1
            className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {t("therapists.title")}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            {t("therapists.subtitle")}
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="bg-background pb-12 pt-6 sm:pb-16 sm:pt-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Filter bar */}
          <div className="-mt-16 mb-6 rounded-2xl border border-border/60 bg-card/95 p-3 shadow-lg backdrop-blur-sm sm:-mt-20 sm:p-4">
            <div className="flex flex-col gap-2.5 md:flex-row md:items-center md:gap-3">
              {/* Search */}
              <div className="relative flex-1">
                <MagnifyingGlass className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder={t("therapists.searchPlaceholder")}
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="h-12 rounded-xl border-border/60 bg-background pl-11 pr-4 text-base shadow-none focus-visible:ring-1 md:text-sm"
                />
              </div>

              {/* Filters: two equal pills on mobile (a sideways scroller hid
                  "clear filters" off-screen), fixed row on md+ */}
              <div className="grid grid-cols-2 gap-2 md:flex md:flex-none md:gap-3">
                <Select
                  value={problemTag || "__all"}
                  onValueChange={(v) =>
                    navigate({
                      to: "/therapists",
                      search: {
                        language,
                        problemTag: v === "__all" ? undefined : (v as typeof problemTag),
                      },
                    })
                  }
                >
                  <SelectTrigger
                    aria-label={t("therapists.topicFilter")}
                    className={cn(
                      filterPillTrigger,
                      "*:data-[slot=select-value]:block *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:flex-1 *:data-[slot=select-value]:truncate *:data-[slot=select-value]:text-left",
                      problemTag && filterPillActive,
                    )}
                  >
                    <Funnel className="size-4 shrink-0 text-muted-foreground" />
                    <SelectValue placeholder={t("therapists.topicFilterAny")} />
                  </SelectTrigger>
                  <SelectContent align="end">
                    <SelectItem value="__all">{t("therapists.topicFilterAny")}</SelectItem>
                    {PROBLEM_TAGS.map((tag) => (
                      <SelectItem key={tag} value={tag}>
                        {PROBLEM_TAG_LABELS[tag]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <LanguageFilterCombobox
                  value={language ?? null}
                  options={languageOptions}
                  anyLabel={t("languages.filterAny")}
                  searchPlaceholder={t("languages.pickerPlaceholder")}
                  ariaLabel={t("languages.filterLabel")}
                  onChange={(code) =>
                    navigate({
                      to: "/therapists",
                      search: { problemTag, language: code ?? undefined },
                    })
                  }
                />

                {/* Mobile-only: clear-all on its own line under the pills */}
                {hasActiveChips && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="col-span-2 inline-flex items-center gap-1.5 justify-self-start px-1 py-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground md:hidden"
                  >
                    <X className="size-3.5" weight="bold" />
                    {t("therapists.filters.clearAll")}
                  </button>
                )}
              </div>
            </div>

            {/* Active filter chips */}
            {hasActiveChips && (
              <div className="mt-3 hidden flex-wrap items-center gap-2 border-t border-border/40 pt-3 md:flex">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Funnel className="size-3.5" weight="fill" />
                  {t("therapists.filters.toggle")}:
                </span>
                {activeTopicName && (
                  <FilterChip
                    label={`${t("therapists.topicFilter")}: ${activeTopicName}`}
                    onRemove={() =>
                      navigate({
                        to: "/therapists",
                        search: { language },
                      })
                    }
                  />
                )}
                {activeLanguageName && (
                  <FilterChip
                    label={`${t("languages.filterLabel")}: ${activeLanguageName}`}
                    onRemove={() =>
                      navigate({
                        to: "/therapists",
                        search: { problemTag },
                      })
                    }
                  />
                )}
                <button
                  type="button"
                  onClick={clearAll}
                  className="ml-auto text-xs font-semibold text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  {t("therapists.filters.clearAll")}
                </button>
              </div>
            )}
          </div>

          {/* Result count */}
          {!isLoading && therapists.length > 0 && (
            <p className="mb-5 text-sm text-muted-foreground">
              {t("therapists.filters.resultCount", { count: therapists.length })}
            </p>
          )}
          {isLoading && therapists.length === 0 ? (
            <div className="grid gap-5 lg:grid-cols-2">
              {[...Array(8)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : therapists.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="flex size-16 items-center justify-center rounded-2xl bg-muted/40">
                <Users className="size-7 text-muted-foreground" />
              </div>
              <h3
                className="mt-4 text-lg font-semibold text-foreground"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {debouncedSearch
                  ? t("therapists.noResults")
                  : t("therapists.noTherapists")}
              </h3>
              <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">
                {debouncedSearch
                  ? t("therapists.noResultsHint")
                  : t("therapists.noTherapistsHint")}
              </p>
            </div>
          ) : (
            <div className="grid gap-5 lg:grid-cols-2">
              {therapists.map((th) => (
                <TherapistCard key={th.id} therapist={th} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const TherapistsIndexSection = defineSection({
  Component: TherapistsIndexPageView,
  queries: [publicTherapistsListQuery],
});

function TherapistsIndexPage() {
  return <TherapistsIndexSection.Component />;
}

export const Route = createFileRoute("/therapists/")({
  ...ssrPage({
    component: TherapistsIndexPage,
    sections: [TherapistsIndexSection],
    meta: therapistsMeta,
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    problemTag: isProblemTag(search.problemTag) ? search.problemTag : undefined,
    language: parsePublicLanguageFilter(search.language),
  }),
});

/* --- Therapist Card --- */

// Language code → flag emoji. Falls back to the uppercased code when a flag
// isn't mapped (keeps the UI honest for rare regional codes).
const LANGUAGE_FLAGS: Record<string, string> = {
  bs: "🇧🇦", en: "🇬🇧", de: "🇩🇪", fr: "🇫🇷", it: "🇮🇹", es: "🇪🇸",
  pt: "🇵🇹", nl: "🇳🇱", pl: "🇵🇱", cs: "🇨🇿", sk: "🇸🇰", sl: "🇸🇮",
  hu: "🇭🇺", ro: "🇷🇴", bg: "🇧🇬", el: "🇬🇷", da: "🇩🇰", sv: "🇸🇪",
  fi: "🇫🇮", no: "🇳🇴", et: "🇪🇪", lv: "🇱🇻", lt: "🇱🇹", ga: "🇮🇪",
  mt: "🇲🇹", sr: "🇷🇸", hr: "🇭🇷", me: "🇲🇪",
};

// Session modality → icon + label key (under the public `therapists` block).
const MODALITY: Record<string, { key: string; Icon: typeof Monitor }> = {
  online: { key: "modalityOnline", Icon: VideoCamera },
  in_person: { key: "modalityInPerson", Icon: MapPin },
  both: { key: "modalityBoth", Icon: Monitor },
};

// Readable gold (hue 84) — the brand's --primary is petrolej/teal, so the warm
// gold used for the name accent, tags and CTA is a custom value, matching the
// approved mock. Soft warm beige backs the tags.
const GOLD = "oklch(52% 0.12 84)";
const GOLD_TAG_BG = "oklch(93% 0.014 84)";
const CARD_SHADOW =
  "0 1px 2px oklch(20% 0.01 60 / 0.04), 0 8px 24px -12px oklch(20% 0.01 60 / 0.16)";
const CARD_SHADOW_HOVER =
  "0 2px 4px oklch(20% 0.01 60 / 0.06), 0 14px 30px -12px oklch(20% 0.01 60 / 0.22)";

function TherapistCard({ therapist }: { therapist: Therapist }) {
  const { t } = useTranslation();
  const topics = topicLabels(therapist.problemTags);
  const languages = therapist.languages?.length ? therapist.languages : [BOSNIAN_CODE];
  const modality = MODALITY[therapist.sessionModality] ?? MODALITY.both!;
  const showOffice =
    !!therapist.officeAddress &&
    (therapist.sessionModality === "in_person" || therapist.sessionModality === "both");
  const initial = therapist.userName?.charAt(0)?.toUpperCase() || "?";

  return (
    <Link
      to="/therapists/$therapistId"
      params={{ therapistId: therapist.slug ?? therapist.id }}
      className="group flex min-w-0 overflow-hidden rounded-[20px] bg-card transition-all duration-300 hover:-translate-y-0.5"
      style={{ boxShadow: CARD_SHADOW }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = CARD_SHADOW_HOVER)}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = CARD_SHADOW)}
    >
      {/* Left: portrait spanning the full card height, edge to edge */}
      <div
        className="relative w-[118px] shrink-0 self-stretch overflow-hidden sm:w-[150px]"
        style={{ background: "linear-gradient(135deg, oklch(90% 0.06 84), oklch(85% 0.1 84))" }}
      >
        <span
          className="absolute inset-0 flex items-center justify-center text-4xl font-bold"
          style={{ fontFamily: "var(--font-heading)", color: GOLD }}
          aria-hidden
        >
          {initial}
        </span>
        {therapist.profileImageUrl && (
          <Img
            src={therapist.profileImageUrl}
            alt={therapist.userName}
            sizes="150px"
            className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        )}
      </div>

      {/* Right: content */}
      <div className="flex min-w-0 flex-1 flex-col p-4 sm:p-[18px]">
        <div className="flex min-w-0 items-start justify-between gap-2">
          <h3
            className="min-w-0 flex-1 truncate text-[18px] font-bold leading-tight text-foreground"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {therapist.userName}
          </h3>
          {therapist.reviewCount > 0 && (
            <div className="flex shrink-0 items-center gap-1 text-[13px] font-bold text-foreground">
              <Star className="size-3.5" weight="fill" style={{ color: GOLD }} />
              <span>{therapist.avgRating?.toFixed(1)}</span>
              <span className="font-normal text-muted-foreground">({therapist.reviewCount})</span>
            </div>
          )}
        </div>

        <p className="mt-1 flex items-center gap-1.5 text-[13px] text-muted-foreground">
          <modality.Icon className="size-4 shrink-0 opacity-75" />
          {t(`therapists.${modality.key}`)}
        </p>

        {/* Tags */}
        {(topics.length > 0 || therapist.isVolunteer) && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {therapist.isVolunteer && (
              <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[12px] font-semibold text-emerald-700">
                Volonter
              </span>
            )}
            {topics.slice(0, 2).map((topic) => (
              <span
                key={topic}
                className="max-w-full truncate rounded-full px-2.5 py-1 text-[12px] font-semibold"
                style={{ background: GOLD_TAG_BG, color: GOLD }}
              >
                {topic}
              </span>
            ))}
            {topics.length > 2 && (
              <span
                className="rounded-full px-2.5 py-1 text-[12px] font-semibold text-muted-foreground"
                style={{ background: GOLD_TAG_BG }}
              >
                +{topics.length - 2}
              </span>
            )}
          </div>
        )}

        {/* Info: languages as flags (hover/tap for the name) + optional office city */}
        <div className="mb-3.5 mt-3 flex flex-wrap items-center gap-x-3.5 gap-y-1.5">
          {languages.length > 0 && (
            <div className="flex min-w-0 items-center gap-1.5">
              <Translate className="size-4 shrink-0 text-muted-foreground opacity-60" />
              <div className="flex items-center gap-1">
                {languages.map((code) => {
                  const name = t(`languages.names.${code}`);
                  const flag = LANGUAGE_FLAGS[code];
                  return (
                    <span
                      key={code}
                      title={name}
                      className="group/flag relative cursor-default leading-none"
                    >
                      {flag ? (
                        <span className="text-[15px]">{flag}</span>
                      ) : (
                        <span className="rounded bg-muted px-1 text-[10px] font-semibold uppercase text-muted-foreground">
                          {code}
                        </span>
                      )}
                      <span className="pointer-events-none absolute bottom-[calc(100%+6px)] left-1/2 z-10 -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[11px] font-semibold text-background opacity-0 shadow-sm transition-all duration-150 group-hover/flag:translate-y-0 group-hover/flag:opacity-100">
                        {name}
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {showOffice && (
            <span className="flex min-w-0 items-center gap-1.5 text-[12.5px] text-muted-foreground">
              <MapPin className="size-4 shrink-0 opacity-75" />
              <span className="truncate">{therapist.officeAddress}</span>
            </span>
          )}
        </div>

        {/* Footer: divider, availability + CTA */}
        <div className="mt-auto flex min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-1.5 border-t border-border pt-3.5">
          <span
            className={cn(
              "flex items-center gap-1.5 text-[12px] font-semibold",
              therapist.acceptingNewClients ? "text-emerald-700" : "text-muted-foreground",
            )}
          >
            <span
              className={cn(
                "size-[7px] rounded-full",
                therapist.acceptingNewClients ? "bg-emerald-600" : "bg-muted-foreground/60",
              )}
            />
            {t(therapist.acceptingNewClients ? "therapists.acceptingNew" : "therapists.notAcceptingNew")}
          </span>
          <span
            className="inline-flex shrink-0 items-center gap-1 text-[14px] font-bold"
            style={{ color: GOLD }}
          >
            {t("therapists.viewProfile")}
            <CaretRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-0.5" weight="bold" />
          </span>
        </div>
      </div>
    </Link>
  );
}

/* --- Language filter combobox --- */

function LanguageFilterCombobox({
  value,
  options,
  anyLabel,
  searchPlaceholder,
  ariaLabel,
  onChange,
}: {
  value: string | null;
  options: { code: string; name: string }[];
  anyLabel: string;
  searchPlaceholder: string;
  ariaLabel: string;
  onChange: (code: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q),
    );
  }, [options, query]);

  const selectedName = value ? options.find((o) => o.code === value)?.name ?? value : null;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            "inline-flex items-center justify-between transition-colors",
            filterPillTrigger,
            selectedName && filterPillActive,
            "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          )}
        >
          <Translate className="size-4 shrink-0 text-muted-foreground" />
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-left",
              selectedName ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {selectedName ?? anyLabel}
          </span>
          <CaretDown className="size-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(20rem,calc(100vw-2rem))] p-0">
        <div className="border-b border-border/40 p-2">
          <div className="relative">
            <MagnifyingGlass className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-9 pl-8 text-sm"
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setOpen(false);
              setQuery("");
            }}
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors",
              value === null
                ? "bg-primary/[0.06] text-foreground"
                : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
            )}
          >
            <span className="flex-1 font-medium">{anyLabel}</span>
            {value === null && <Check className="size-3.5 shrink-0 text-primary" />}
          </button>
          <div className="my-1 border-t border-border/30" />
          {filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">—</p>
          ) : (
            filtered.map((o) => {
              const active = o.code === value;
              return (
                <button
                  key={o.code}
                  type="button"
                  onClick={() => {
                    onChange(o.code);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors",
                    active
                      ? "bg-primary/[0.06] text-foreground"
                      : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
                  )}
                >
                  <span className="flex-1 truncate font-medium">{o.name}</span>
                  {active && <Check className="size-3.5 shrink-0 text-primary" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* --- Filter chip --- */

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
      style={{ background: "oklch(37.12% 0.059 186.62 / 0.14)", color: "oklch(45% 0.12 84)" }}
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="-mr-1 flex size-4 items-center justify-center rounded-full transition-colors hover:bg-white/70"
        aria-label="Remove"
      >
        <X className="size-3" weight="bold" />
      </button>
    </span>
  );
}

/* --- Skeleton --- */

function SkeletonCard() {
  return (
    <div className="flex overflow-hidden rounded-[20px] border bg-card">
      <div className="w-[118px] shrink-0 animate-pulse self-stretch bg-muted sm:w-[150px]" />
      <div className="flex min-w-0 flex-1 flex-col p-4 sm:p-[18px]">
        <div className="flex items-start justify-between gap-2">
          <div className="h-5 w-32 animate-pulse rounded bg-muted" />
          <div className="h-4 w-16 animate-pulse rounded bg-muted/60" />
        </div>
        <div className="mt-2 h-3 w-24 animate-pulse rounded bg-muted/60" />
        <div className="mt-2 flex gap-1.5">
          <div className="h-5 w-20 animate-pulse rounded-full bg-muted/40" />
          <div className="h-5 w-16 animate-pulse rounded-full bg-muted/40" />
        </div>
        <div className="mt-2 space-y-1.5">
          <div className="h-3 w-full animate-pulse rounded bg-muted/30" />
          <div className="h-3 w-4/5 animate-pulse rounded bg-muted/30" />
        </div>
        <div className="mt-auto flex items-center justify-between pt-3">
          <div className="h-3 w-24 animate-pulse rounded bg-muted/40" />
          <div className="h-3 w-16 animate-pulse rounded bg-muted/40" />
        </div>
      </div>
    </div>
  );
}

/* --- Helpers --- */

function topicLabels(tags: unknown): string[] {
  if (!Array.isArray(tags)) return [];
  return tags.filter(isProblemTag).map((tag) => PROBLEM_TAG_LABELS[tag]);
}
